/* eslint-disable sonarjs/no-nested-functions */
import { create } from 'zustand';
import { produce } from 'immer';
import { devtools, subscribeWithSelector /*, persist*/ } from 'zustand/middleware';
import { EventDateInfo, PromoStatusEnum } from '../mvcapi/models/common';
import { addTicketsToCartNewSale, clearUnselectedTickets, getEventDateInfo, unselectSpecificTickets } from '../mvcapi/buy-ticket-api';
import { SelectedTicketInfo, SelectedTicketsByEventDateId } from '../logic/models';
import { forEach, remove, some, uniqueId } from 'lodash';
import { AddTicketsToCartResult, AddToCartRequest, AddToCartRequestBase, AddToCartRequestType, PriceQuantity } from '../mvcapi/models/buyticket/ticketselection';
import { connectStoreEffects, StateCreatorWithDevtools, StoreEffect } from './store-utils';
import type { PriceGroupModel, TicketPriceSingleSelectionPanel } from '../mvcapi/models/buyticket/ticketpriceselection';
import { PriceChangeSuggestion } from '../mvcapi/models/buyticket';
import { createAddToCartRequest, createAddToCartRequestFromDateTickets } from '../logic/sales-util';
import { getExpiredTicketsInWarningReport } from '../logic/buytickets';
import { AddPackageToCartStoreSlice, AddPackageToCartStoreSliceDef } from './add-to-cart-stores/add-package-to-cart-store-slice';
import { AddToCartItem, AddToCartItemType, AddToCartTicketItem, EventDateCartItem, 
        findItemById, findItemByTicketId, findTicketInItemsByTicketId, getAddToCartItemByEventDateId, 
        getAllAddToCartItemsFromTickets, getAllEventDateItemsFromItems, getEventDateItemsFromItems, 
        getTicketsByEventDateId, getTicketsFromItem, getTicketsFromItems } from './add-to-cart-stores/models/add-to-cart-items';
import { EMPTY_UUID } from '../logic';
import { CreditCardConditionSeenStoreSlice, CreditCardConditionSeenStoreSliceDef } from './add-to-cart-stores/credit-card-condition-seen-store-slice';
import { VenuePlanSearchStoreSlice, VenuePlanSearchStoreSliceDef } from './add-to-cart-stores/venue-plan-search-store-slice';
import { TicketSelectionSlice, TicketSelectionSliceDef } from './add-to-cart-stores/ticket-selection-slice';
import { PromoStoreSlice, PromoStoreSliceDef } from './add-to-cart-stores/promo-store-slice';
import { WarningReportStoreSlice, WarningReportStoreSliceDef } from './add-to-cart-stores/warning-report-store-slice';
import { MembershipStoreSlice, MembershipStoreSliceDef } from './add-to-cart-stores/membership-store-slice';
import { getSelectedLastPriceGroup, getTicketPriceSingleSelectionPanel } from '../mvcapi/ticket-price';
import toastr from 'toastr';
import { TicketModel } from '../mvcapi/models/buyticket/submodels';

// TODO: Full reset of the store when the user exit the unreistricted ticket selection component.

// INFO: New sale

type SEARCH_MODE = SearchModeEnum.UNDECIDED | SearchModeEnum.VENUE_PLAN | SearchModeEnum.ADVANCED_SEARCH;
export enum SearchModeEnum
{
    UNDECIDED = "UNDECIDED",
    VENUE_PLAN = "VENUE_PLAN",
    ADVANCED_SEARCH = "ADVANCED_SEARCH"
}

export type AddTicketsToCartStoreAllSlices = AddTicketsToCartStore & TicketSelectionSlice & MembershipStoreSlice & VenuePlanSearchStoreSlice & WarningReportStoreSlice & PromoStoreSlice & CreditCardConditionSeenStoreSlice & AddPackageToCartStoreSlice;
const slices: any = [ VenuePlanSearchStoreSliceDef, TicketSelectionSliceDef, MembershipStoreSliceDef, WarningReportStoreSliceDef, PromoStoreSliceDef, CreditCardConditionSeenStoreSliceDef, AddPackageToCartStoreSliceDef ];

export interface AddTicketsToCartStore {

    /** Error that could happen during loading process of programs, event, event dates, ... */
    loadingError: string,

    eventDateId: number,
    loadEventDate: (eventDateId: number) => Promise<void>;
    eventDate: EventDateInfo,
    fetchingEventDate: boolean,

    preferredSelectedSectionId: number;
    setPreferredSelectedSectionId: (sectionId: number) => void;

    // After added to cart callback
    afterAddedToCartCallback: (placeToGo: string) => void,
    setAfterAddedToCartCallback: (cb: (placeToGo: string) => void) => void;
    
    // Selected tickets management =========================================
    /** @deprecated Use the items instead. (This is now and temporary a calculated field) */
    selectedTicketsByEventDateId: SelectedTicketsByEventDateId, __recalculateSelectedTicketsByEventDateId: StoreEffect<any>, // CALCULATED FIELD
    
    // Event date items
    eventDateItems: AddToCartItem[],

    /** Utiliser pour récupérer le panel de liste de prix */
    pricePanel: TicketPriceSingleSelectionPanel,
    fetchingPricePanel: boolean,
    refreshPricesListPanel: (addToCartItems: AddToCartItem[]) => Promise<void>,

    //setSelectedTicketsByEventDateId: (selectedTicketsByEventDateId: SelectedTicketsByEventDateId) => void;
    // _addSelectedTickets: (eventDateId: number, newSelectedTickets: SelectedTicketInfo[]) => void;
    // _updateEventDateTickets: (eventDateId: number, newSelectedTickets: SelectedTicketInfo[]) => void;


    _addSelectedItems: (eventDateId: number, newSelectedItems: AddToCartItem[]) => void;
    _updateEventDateItems: (eventDateId: number, newSelectedItems: AddToCartItem[]) => void;


    // /**
    //  * Save the selected tickets in the state.
    //  * It also updates seen credit card conditions for every required price.
    //  * @param associatedTicketsWithPrices The tickets with their selected prices
    //  * @param isUpdate Determines if the selection is for updating a specific ticket or add new ones
    //  */
    // saveSelectedTickets: (associatedTicketsWithPrices: SelectedTicketInfo[], isUpdate?: boolean) => void;

    /**
     * Save the selected tickets in the state.
     * It also updates seen credit card conditions for every required price.
     * @param associatedItemsWithPrices The items with their selected prices
     * @param isUpdate Determines if the selection is for updating a specific ticket or add new ones
     */
    saveSelectedItems: (associatedItemsWithPrices: AddToCartItem[], isUpdate?: boolean) => void;

    removeTickets: (ticketIdsToRemove: number[], unselect: boolean) => Promise<boolean>;
    clearAllTickets: (withUnselect: boolean) => void;
    clearSelectedTickets: () => Promise<void>;

    /** Call that when the user has selected the price of the tickets */
    _runPriceSelectionForNewItems: (addToCartItems: AddToCartItem[]) => Promise<boolean>,

    /** Used to validate if we ca switch directly to add ticket to cart */
    addTicketWithoutPriceSelection: (pricePanel: TicketPriceSingleSelectionPanel) => boolean;

    /** Utilisé après avoir trouver des billets */
    addNewTickets: (foundTickets: TicketModel[]) => Promise<boolean>;

    /** Start the modification of a single ticket (Expecially its price) */
    startItemModification: (itemId: string) => void;
    // startTicketModification: (ticketId: number) => Promise<void>;
    
    // /* When the TicketSelectionSlice has done its job */
    // handleTicketsSelected: (associatedTicketsWithPrices: SelectedTicketInfo[], isUpdate?: boolean) => void;

    /* When the TicketSelectionSlice has done its job */
    handleItemsSelected: (associatedTicketsWithPrices: AddToCartItem[], isUpdate?: boolean) => void;
    setShowTicketSearchWizard: (showTicketSearchWizard: boolean) => void;
    searchTicketMode: SEARCH_MODE;
    setSearchTicketMode: (searchTicketMode: SEARCH_MODE) => void;

    //** Store the last price group to using it on next ticket
    lastSelectedPriceGroup: PriceGroupModel;
    setLastSelectedPriceGroup: (lastSelectedPriceGroup: PriceGroupModel) => void;

    hasSelectedItems: () => boolean;
    removeAllTickets: () => void;
    confirmRemoveTicketsProcess: () => Promise<void>;

    confirmPriceSelectionCompleted: (addToCartItems: AddToCartItem[], pricesQty: PriceQuantity[]) => void;

    // Price List ==========================================================
    /** Apply price change suggestion sent by the server. Like when we add a new membership cards, we want our tickets the use the unlocked price */
    applyPriceChangeSuggestions: (priceChangeSuggestions: PriceChangeSuggestion[]) => void;

    // Add to cart =========================================================
    /** Add the selected tickets to the cart */
    getAddToCartRequest: () => AddToCartRequest;

    /** Add tickets to cart. This is the final action. */
    addToCart: () => Promise<AddToCartStoreResult>;
}

const AddTicketsToCartStoreDef: StateCreatorWithDevtools<AddTicketsToCartStoreAllSlices, AddTicketsToCartStore> = (set, get) => {
    

    return {
        // ****************
        // States
        // ****************
        loadingError: "",

        eventDateId: 0,
        eventDate: null,
        fetchingEventDate: false,

        preferredSelectedSectionId: 0,

        setPreferredSelectedSectionId: (sectionId: number) => {
            set({ preferredSelectedSectionId: sectionId }, false, "setPreferredSelectedSectionId");
        },



        // Callbacks
        afterAddedToCartCallback: null,
        setAfterAddedToCartCallback: (cb: (placeToGo: string) => void) => set({ afterAddedToCartCallback: cb }, false, "afterAddedToCartCallback"),
        setShowTicketSearchWizard: (showTicketSearchWizard: boolean) => set({ showTicketSearchWizard }, false, "showTicketSearchWizard"),

        selectedTicketsByEventDateId: {},

        eventDateItems: [],

        pricePanel: null,
        fetchingPricePanel: false,



        searchTicketMode: SearchModeEnum.UNDECIDED,
        setSearchTicketMode: (searchTicketMode: SEARCH_MODE) => set({ searchTicketMode }, false, "setSearchTicketMode"),
        
        lastSelectedPriceGroup: null,
        setLastSelectedPriceGroup: (lastSelectedPriceGroup: PriceGroupModel) => set({ lastSelectedPriceGroup }, false, "setSelectedPriceGroup"),

        // ****************
        // Actions
        // ****************

        loadEventDate: async (eventDateId: number) => {
            set({ eventDateId: eventDateId, fetchingEventDate: false }, false, { type: "loadEventDate", eventDateId });

            console.log("Reset last selected price group...");
            set({ lastSelectedPriceGroup: null}, false, "loadEventDate/resetLastSelectedPriceGroup");

            if (eventDateId) {
                set({ fetchingEventDate: true }, false, "loadEventDate/start");

                try {
                    const result = await getEventDateInfo(eventDateId);

                    set({ fetchingEventDate: false, eventDate: result }, false, "loadEventDate/success");


                    // set({
                    //     handleItemsSelectedExternalLogic: { cb: get().handleItemsSelected }
                    // }, false, "loadEventDate/handleItemsSelectedExternalLogic");

                } catch(err) {
                    set({ fetchingEventDate: false, loadingError: err.message }, false, "loadEventDate/error");

                    /*
                    this.raiseContentChanged();
                    */
                }
            }
        },

        //setSelectedTicketsByEventDateId: (selectedTicketsByEventDateId: SelectedTicketsByEventDateId) => set({ selectedTicketsByEventDateId }),

        // _addSelectedTickets: (eventDateId: number, newSelectedTickets: SelectedTicketInfo[]) => {
        //     // const selectedTickets = getTicketItemsByEventDateId(get().items, eventDateId);
            
        //     // Add new tickets to the list (Remove existing ticket and re-add them if needed)
        //     const itemsToRemove = newSelectedTickets.map(ticket => findItemByTicketId(get().eventDateItems, ticket.ticketId)).filter(it => it != null);
        //     const itemsToAdd = newSelectedTickets.map(ticket => ({ _id: uniqueId(), type: AddToCartItemType.Ticket, ticket: ticket, eventDateId: eventDateId }));
    
        //     //Before : get().updateEventDateTickets(eventDateId, newList);
        //     {
        //         get().clearCartWarningReport();
    
        //         set(produce((draft: AddTicketsToCartStore) => {
        //             itemsToRemove.forEach(itemToRemove => {
        //                 remove(draft.eventDateItems, it => it === itemToRemove);
        //             });
                    
        //             draft.eventDateItems.push(...itemsToAdd);
        //         }), false, "updateEventDateTickets");   
        //     }

        //     get().setPlanFocusPointFromTickets(newSelectedTickets);
        // },

        _addSelectedItems: (eventDateId: number, newSelectedItems: AddToCartItem[]) => {
            const newSelectedTickets = getTicketsFromItems(newSelectedItems);
            
            // Add new tickets to the list (Remove existing ticket and re-add them if needed)
            const itemsToRemove = newSelectedItems.map(item => findItemByTicketId(get().eventDateItems, (item as AddToCartTicketItem).ticket.ticketId)).filter(it => it != null);
            const itemsToAdd = newSelectedItems.map(item => ({ _id: uniqueId(), type: AddToCartItemType.Ticket, ticket: (item as AddToCartTicketItem).ticket, eventDateId: eventDateId }));
    
            //Before : get().updateEventDateTickets(eventDateId, newList);
            {
                get().clearCartWarningReport();
    
                set(produce((draft: AddTicketsToCartStore) => {
                    itemsToRemove.forEach(itemToRemove => {
                        remove(draft.eventDateItems, it => it === itemToRemove);
                    });
                    
                    draft.eventDateItems.push(...itemsToAdd);
                }), false, "updateEventDateTickets");   
            }

            get().setPlanFocusPointFromTickets(newSelectedTickets);
        },

        // _updateEventDateTickets: (eventDateId: number, newSelectedTickets: SelectedTicketInfo[]) => {
        //     // Remove all the event date items
        //     set(produce((draft: AddTicketsToCartStore) => {
        //         const itemsToRemove = getEventDateItemsFromItems(draft.eventDateItems, eventDateId);

        //         itemsToRemove.forEach(itemToRemove => {
        //             remove(draft.eventDateItems, it => it === itemToRemove);
        //         });

        //         // Add new tickets to the list
        //         newSelectedTickets.forEach(ticket => {
        //             draft.eventDateItems.push({ _id: uniqueId(), type: AddToCartItemType.Ticket, ticket: ticket, eventDateId: eventDateId } as AddToCartTicketItem);
        //         });

        //     }), false, { type: "updateEventDateTickets/updateItems", eventDateId });
        // },


        _updateEventDateItems: (eventDateId: number, newSelectedItems: AddToCartItem[]) => {
            // Remove all the event date items
            set(produce((draft: AddTicketsToCartStore) => {
                const itemsToRemove = getEventDateItemsFromItems(draft.eventDateItems, eventDateId);

                itemsToRemove.forEach(itemToRemove => {
                    remove(draft.eventDateItems, it => it === itemToRemove);
                });

                // Add new tickets to the list
                newSelectedItems.forEach((item: AddToCartTicketItem) => {
                    draft.eventDateItems.push({ _id: uniqueId(), type: AddToCartItemType.Ticket, ticket: item.ticket, eventDateId: item.eventDateId } as AddToCartTicketItem);
                });

            }), false, { type: "updateEventDateTickets/updateItems", eventDateId });
        },

        addNewTickets: async (foundTickets: TicketModel[]) => {
            // Préparer les tickets pour la sélection
            const tickets: SelectedTicketInfo[] = foundTickets.map((ticket) => {
                return { ticketId: ticket.TicketID, ticket: ticket };
            });

            const addToCartItems = getAllAddToCartItemsFromTickets(get().eventDateId, tickets);

            return get()._runPriceSelectionForNewItems(addToCartItems);
        },

        refreshPricesListPanel: async (addToCartItems: AddToCartItem[]) => {
            set({ fetchingPricePanel: true }, false, "setfetchingpricepanel/true");

            //Check if there is only one price group, so we can skip the price selection modal
            const pricePanel: TicketPriceSingleSelectionPanel = await getTicketPriceSingleSelectionPanel(addToCartItems, createAddToCartRequest(get().eventDateId, get().selectedTicketsByEventDateId), [get().eventDateId]);
            
            set({pricePanel, fetchingPricePanel: false }, false, "setPricePanel/fetchingPricePanel");
        },

        confirmPriceSelectionCompleted: (addToCartItems: AddToCartItem[], pricesQty: PriceQuantity[]) => {
            get().confirmPriceSelection(addToCartItems, pricesQty, null);
            get().setAutoFindTicket(null);
        },

        /** 
        * Callback for when a client has selected a seat. Here, the price is not selected yet
        * and is `undefined` in the {@link SelectedTicketInfo} object.
        * @param addToCartItems The array of add to cart items. The array can come from differents places (TODO: List places here)
        * @returns `true` if selection is successful ; `false` otherwise.
        */
        _runPriceSelectionForNewItems : async (addToCartItems: AddToCartItem[]): Promise<boolean> => {
    
            // Prepare the final confirm callback and put it in a state
            const confirmCallback = new Promise((resolve: (bool) => void) => { 
                set( { addNewTicketConfirmCallback: { cb: resolve }}, false, "addNewTickets/confirmCallback" ); 
            });
            
            // Get the price panel for the selected tickets
            await get().refreshPricesListPanel(addToCartItems);
            const pricePanel = get().pricePanel;

            //If the user has already selected a price group, we verify to use it
            const lastSelectedPriceGroup = getSelectedLastPriceGroup(useAddTicketsToCartStore.getState().lastSelectedPriceGroup, pricePanel.priceGroups);
        
            const pricesQty: PriceQuantity = { quantity: addToCartItems.length };

            if(lastSelectedPriceGroup && !get().eventDate.AlwaysAskForPriceOnTicketSelection) {
                pricesQty.priceGroup = lastSelectedPriceGroup;

                set({ticketPriceSelectionEditMode: false}, false, "setticketpriceselectioneditmode/false");
                get().confirmPriceSelectionCompleted(addToCartItems, [pricesQty]);
            }
            else{
                if (pricePanel !== null && get().addTicketWithoutPriceSelection(pricePanel)) {
                    pricesQty.priceGroup = pricePanel.priceGroups.find(pg => !pg.Disabled);

                    set({ticketPriceSelectionEditMode: false}, false, "setticketpriceselectioneditmode/false");
                    get().confirmPriceSelectionCompleted(addToCartItems, [pricesQty]);
                }
                else {
                    // In here, the price is not selected yet and is undefined in the selectedTicket object
                    get().showTicketPriceSelectionModal(addToCartItems, false);
                }
            }
            
            set({searchingTicket: false}, false, "setsearchingticket/false");

            // Return this handle to the caller.
            return confirmCallback;
        },

         
        handleItemsSelected: (selectedItemsWithPrice: AddToCartItem[], editMode: boolean = false) => {
            if(editMode) {
                if (selectedItemsWithPrice.length > 0) {
                    let editedItem = selectedItemsWithPrice[0] as AddToCartTicketItem;

                    // TODO: Pourrait peut etre resté au niveau des items au lieu des tickets
                    let eventDateItems = getAddToCartItemByEventDateId(get().eventDateItems, get().eventDateId);
    
                    let itemIndex = eventDateItems.findIndex(item => (item as AddToCartTicketItem).ticket.ticketId === editedItem.ticket.ticketId);
                    if (itemIndex >= 0) {
                        eventDateItems = [...eventDateItems];
                        eventDateItems[itemIndex] = editedItem;
                        get().saveSelectedItems(eventDateItems, true);
                    }
                }
    
                // End verison modif
            } else { // New tickets
                get().saveSelectedItems(selectedItemsWithPrice);
            }
    
        },
        
        // handleTicketsSelected: (selectedTicketsWithPrice: SelectedTicketInfo[], editMode: boolean = false) => {
        //     if(editMode) {
        //         if (selectedTicketsWithPrice.length > 0) {
        //             let editedTicket = selectedTicketsWithPrice[0];

        //             // TODO: Pourrait peut etre resté au niveau des items au lieu des tickets
        //             let eventDateTickets = getTicketsByEventDateId(get().eventDateItems, get().eventDateId);
    
        //             let ticketIndex = eventDateTickets.findIndex(st => st.ticketId === editedTicket.ticketId);
        //             if (ticketIndex >= 0) {
        //                 eventDateTickets = [...eventDateTickets];
        //                 selectedTicketsWithPrice[ticketIndex] = editedTicket;
        //                 get().saveSelectedTickets(eventDateTickets, true);
        //             }
        //         }
    
        //         // End verison modif
        //     } else { // New tickets
        //         get().saveSelectedTickets(selectedTicketsWithPrice);
        //     }
    
        // },


        saveSelectedItems: (associatedItemsWithPrices: AddToCartItem[], isUpdate: boolean = false) => {
            if(!associatedItemsWithPrices || associatedItemsWithPrices.length === 0) return;
            const pgs = associatedItemsWithPrices.map(item => (item as AddToCartTicketItem).ticket.pricegroup);

            get().setCreditCartConditionsAsSeen(pgs);

            if (isUpdate) {
                get()._updateEventDateItems(get().eventDateId, [...associatedItemsWithPrices]);
            } else {
                get()._addSelectedItems(get().eventDateId, associatedItemsWithPrices);
            }
        },


        // saveSelectedTickets: (associatedTicketsWithPrices: SelectedTicketInfo[], isUpdate: boolean = false) => {
        //     if(!associatedTicketsWithPrices || associatedTicketsWithPrices.length === 0) return;
        //     const pgs = associatedTicketsWithPrices.map(t => t.pricegroup);

        //     get().setCreditCartConditionsAsSeen(pgs);

        //     if (isUpdate) {
        //         get()._updateEventDateTickets(get().eventDateId, [...associatedTicketsWithPrices]);
        //     } else {
        //         get()._addSelectedTickets(get().eventDateId, associatedTicketsWithPrices);
        //     }
        // },

        removeTickets: async (ticketIdsToRemove, unselect: boolean) => {
            if(unselect)
                await unselectSpecificTickets(ticketIdsToRemove);
            
            // TODO: Support TicketPacks when we will be working on that
            set(produce((draft: AddTicketsToCartStore) => {
                const itemsToRemove = draft.eventDateItems
                    //.filter(it => getEventDateItemFromItem(it, eventDateId)) // Only for the event date
                    .filter(it => it.type === AddToCartItemType.Ticket) // Only works on simple ticket item
                    .filter((it: AddToCartTicketItem) => some(ticketIdsToRemove, id => id === it.ticket.ticketId)); // Only the tickets to remove

                itemsToRemove.forEach(itemToRemove => {
                    remove(draft.eventDateItems, it => it === itemToRemove);
                });
            }), false, { type: "removeTickets", ticketIdsToRemove });

            get().clearCartWarningReport();
            get().forcePlanRefresh();


            console.log("Reset last selected price group...");
            // Remove the last price group to force the user to select a new one
            set({lastSelectedPriceGroup: null}, false, "removeTickets/lastSelectedPriceGroup");

            return true;
        },

        hasSelectedItems: () => {
            return get().eventDateItems.length > 0;
        },

        removeAllTickets: () => {
            const ticketIdsToRemove = getTicketsFromItems(get().eventDateItems).map(t => t.ticket.TicketID);
            get().startTicketRemovalProcessWithConfirm(ticketIdsToRemove);
        },

        clearAllTickets: (withUnselect: boolean = true) => {
            set({ eventDateItems: [] }, false, "clearAllTickets");
            if(withUnselect)
                clearUnselectedTickets();
        },

        confirmRemoveTicketsProcess: async () => {
            await get().removeTickets(get().ticketsAwaitingForRemoval.ticketIds, true);

            set({ 
                ticketsAwaitingForRemoval: null,
                showRemoveTicketsConfirmPopup: false,
                searchTicketErrorMessage: null,
            });
        },

        getAddToCartRequest: () => {
            const tickets = getTicketsByEventDateId(get().eventDateItems, get().eventDateId);
            return createAddToCartRequestFromDateTickets(tickets, AddToCartRequestType.EventDate);
        },

        // Ticket modification process
        addTicketWithoutPriceSelection: (pricePanel: TicketPriceSingleSelectionPanel, ) => {

            // Ancienne condition!! // Quand on a la proposition de membership ça fonctionne quand meme car le prix membre est hidden ce qui fait qu'il contient 2 prix et affiche la liste de prix
            // const bypassPriceSeletion =  pricePanel.priceGroups.length === 1 && get().eventDate.PromoInfo?.PromoID === EMPTY_UUID && pricePanel.activeMembershipCards.length === 0 && !pricePanel.sectionSpecialMessage;
            
            const bypassPriceSeletion = 
                pricePanel.priceGroups.filter(pg => !pg.Disabled).length === 1 // On bypass seulement s'il n'y a qu'un seul prix excluant les disabled 
                && (get().eventDate.PromoInfo?.PromoStatus != PromoStatusEnum.PasswordNeeded || get().eventDate.PromoInfo?.PromoID !== EMPTY_UUID) // Verifie si pas d'offre de promo ou promo déjà active
                && (pricePanel.priceGroups.filter(pg => pg.RequiresMembership).length === 0 || pricePanel.activeMembershipCards.length === 1) // Verifie si pas d'offre de membership carte ou carte déjà active
                && !pricePanel.sectionSpecialMessage; // Pas de speciale message à afficher au client

            set({showTicketPriceSelection: !bypassPriceSeletion});
            return bypassPriceSeletion;
        },
        /** When the user clicks on a ticket to modify its price */
        startItemModification: async (itemId: string) => {
            const item = findItemById(get().eventDateItems, itemId);

            if (item) {
                get().refreshPricesListPanel([item]);
                get().showTicketPriceSelectionModal([Object.assign({}, item)], true);
            }
        },

        clearSelectedTickets: async (): Promise<void> => {
            //const tickets = get().selectedTicketsByEventDateId[get().eventDateId]?.selectedTickets || [];
            // TODO: Pourrait fonctionner en item à la place
            const tickets = getTicketsByEventDateId(get().eventDateItems, get().eventDateId);
            if (tickets.length > 0) {
                await get().removeTickets(tickets.map(t => t.ticketId), true);
            }
        },


        // Price list
        applyPriceChangeSuggestions: (priceChangeSuggestions: PriceChangeSuggestion[]) => {
            if(priceChangeSuggestions && priceChangeSuggestions.length > 0) {
    
                set({ 
                    eventDateItems: produce(get().eventDateItems, draft => {
                        forEach(priceChangeSuggestions, (suggestion) => {
                            const ticket = findTicketInItemsByTicketId(draft, suggestion.ticketId);
                            if(ticket) {
                                ticket.priceId = suggestion.newPriceId;
                                ticket.pricegroup = suggestion.newPrice;
                            }
                        });  
                    }
                )});
            }
        },

        // Add to cart
        addToCart: async (): Promise<AddToCartStoreResult> => {
            //const addToCartRequest = createAddToCartRequest(get().eventDateId, get().selectedTicketsByEventDateId);
            const tickets = getTicketsByEventDateId(get().eventDateItems, get().eventDateId);
            const addToCartRequest = createAddToCartRequestFromDateTickets(tickets, AddToCartRequestType.EventDate);

            const serverResult = await addTicketsToCartNewSale(addToCartRequest);

            const result = {
                addToCartRequest,
                result: serverResult,
                expiredTicketIDs: null,
                expiredTicketWarningMessage: null,
            };

            if ((!serverResult.IsSuccess && !serverResult.RedirectToInvalidOperation && !serverResult.MustRedirectToCart)
                && (serverResult.warningsReport?.generalWarnings?.length > 0 
                    || serverResult.warningsReport?.priceListWarnings?.length > 0)
            )
            {
                get().setAddToCartWarningReport(serverResult.warningsReport);

                let expiredTicketsWarning = getExpiredTicketsInWarningReport(serverResult.warningsReport, false);
                if(expiredTicketsWarning.expiredTicketIds.length > 0) {
                    await get().removeTickets(expiredTicketsWarning.expiredTicketIds, false);
                    result.expiredTicketIDs = expiredTicketsWarning.expiredTicketIds;
                    result.expiredTicketWarningMessage = expiredTicketsWarning.warningMessage;
                }
            }

            return result;
        },

        __recalculateSelectedTicketsByEventDateId: new StoreEffect<AddTicketsToCartStore>(
            state => state.eventDateItems
        , () => {
            const newDict: SelectedTicketsByEventDateId = {};
        
            const edItems = getAllEventDateItemsFromItems(get().eventDateItems);
            edItems.forEach(edItem => {
                const edId = (edItem as EventDateCartItem).eventDateId;
                if(!newDict[edId]) 
                    newDict[edId] = {
                        eventDateId: edId,
                        selectedTickets: [],
                    };
        
                newDict[edId].selectedTickets.push(...getTicketsFromItem(edItem));
            });
        
            // console.log("Auto-updating selectedTicketsByEventDateId with ", newDict);
        
            set({
                selectedTicketsByEventDateId: newDict,
            }, false, "_recalculateSelectedTicketsByEventDateId");
            
        }),

    };
};
slices.push(AddTicketsToCartStoreDef);

export type AddToCartStoreResult = {
    result: AddTicketsToCartResult,
    addToCartRequest: AddToCartRequestBase,
    expiredTicketIDs: null | number[],
    expiredTicketWarningMessage: null | string,
}

export const useAddTicketsToCartStore = connectStoreEffects(create<AddTicketsToCartStoreAllSlices>()(
    subscribeWithSelector(
        devtools(
            (...a) => {
                return slices.reduce((acc, slice) => {
                    return {
                        ...acc,
                        ...slice(...a),
                    };
                }, {});
                // return {
                //     ...AddTicketsToCartStoreDef(...a),
                //     ...CreditCardConditionSeenStoreSliceDef(...a),
                //     ...AddPackageToCartStoreSliceDef(...a),
                // }
            }, 
            { 
                name: 'AddTicketsToCartStore',
                store: 'AddTicketsToCartStore'
            }
        )
    )
));




